import React, { useEffect } from 'react';

import { SquarespaceBriefFields } from '__generated__/SquarespaceBriefFields';
import {
    SquarespaceAssets,
    SquarespaceHandlePayments,
    SquarespaceNeeds,
    SquarespaceOrganizationSize,
} from '__generated__/types';
import ContentSetting from 'projects/components/ContentSetting';
import { formatAndLinkify } from 'projects/components/FormatAndLinkify';
import { linkify } from 'projects/components/Linkify';
import SummaryCard from 'projects/components/summary/SummaryCard';
import SummaryCardItem from 'projects/components/summary/SummaryCardItem';
import { trackEditBriefEvent, trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import { getIndustry } from './industry';

const needs = (needs: SquarespaceNeeds) => {
    switch (needs) {
        case SquarespaceNeeds.NEW_SITE:
            return __('Create a brand new site on Squarespace.');
        case SquarespaceNeeds.RECREATE_SITE:
            return __('Recreate an existing site from another service on Squarespace.');
        case SquarespaceNeeds.MINOR_CHANGES:
            return __(
                'Make minor changes to an existing Squarespace site. (Add or update a page, form, or similar.)'
            );
        case SquarespaceNeeds.FINISH_TRIAL_SITE:
            return __('Finish creating an existing Squarespace trial site.');
        case SquarespaceNeeds.REDESIGN_SITE:
            return __(
                'Completely redesign an existing Squarespace site. (New fonts, colors or styles.)'
            );
        case SquarespaceNeeds.MAJOR_CHANGES:
            return __(
                'Make major changes to an existing Squarespace site. (Add or update multiple pages.)'
            );
        default:
            return needs;
    }
};

const handlePayments = (handlePayments: SquarespaceHandlePayments) => {
    switch (handlePayments) {
        case SquarespaceHandlePayments.YES:
            return __('Yes');
        case SquarespaceHandlePayments.NO:
            return __('No');
        default:
            return handlePayments;
    }
};

const assets = (assets: SquarespaceAssets[]) => {
    return assets.map(asset => {
        switch (asset) {
            case SquarespaceAssets.IMAGES_VIDEO:
                return <div>{__('Image or Video')}</div>;
            case SquarespaceAssets.COPY:
                return <div>{__('Copy')}</div>;
            case SquarespaceAssets.LOGO:
                return <div>{__('Logo')}</div>;
            default:
                return <div>{asset}</div>;
        }
    });
};

const timeline = (timeline: any) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(new Date(timeline));
};

const organizationSize = (organizationSize: SquarespaceOrganizationSize) => {
    switch (organizationSize) {
        case SquarespaceOrganizationSize.ONE:
            return __('Just me');
        case SquarespaceOrganizationSize.TWO_TO_FOUR:
            return __('2-4');
        case SquarespaceOrganizationSize.FIVE_TO_NINE:
            return __('5-9');
        case SquarespaceOrganizationSize.TEN_OR_MORE:
            return __('10+');
        default:
            return organizationSize;
    }
};

interface Props {
    brief: {
        id: string;
        fields: SquarespaceBriefFields;
    };
}

const SummaryPage: (props: Props) => React.ReactElement<Props> = ({ brief }) => {
    useEffect(
        () =>
            trackViewedBriefEvent({
                id: brief.id,
                page: 'Summary',
            }),
        []
    );

    const handleEditClick = (page: 'AboutYou' | 'Details' | 'TimeAndBudget') =>
        trackEditBriefEvent({
            id: brief.id,
            page: page,
        });

    const { fields } = brief;

    return (
        <ContentSetting>
            <SummaryCard
                heading={__('Tell us about your Squarespace project')}
                onEditClick={() => handleEditClick('Details')}
                url={routes.brief(brief, 1)}
            >
                <SummaryCardItem title={__('Project name')}>{fields.title}</SummaryCardItem>
                {fields.needs && (
                    <SummaryCardItem title={__('What describes your needs the best?')}>
                        {needs(fields.needs)}
                    </SummaryCardItem>
                )}
                {fields.handlePayments && (
                    <SummaryCardItem
                        title={__(
                            'Do you want to sell products or services, or accept donations on your site?'
                        )}
                    >
                        {handlePayments(fields.handlePayments)}
                    </SummaryCardItem>
                )}
                {fields.description && (
                    <SummaryCardItem title={__("What's your website for?")}>
                        {formatAndLinkify(fields.description)}
                    </SummaryCardItem>
                )}
                {fields.url && (
                    <SummaryCardItem title={__("If you have an existing website, what's the URL?")}>
                        {linkify([fields.url])}
                    </SummaryCardItem>
                )}
                {fields.assets.length > 0 && (
                    <SummaryCardItem
                        title={__(
                            'Do you already have any of the following? Select all that apply.'
                        )}
                    >
                        {assets(fields.assets)}
                    </SummaryCardItem>
                )}
                {fields.inspirationUrls.length > 0 && (
                    <SummaryCardItem
                        title={__("Are there any websites you'd like to use for inspiration?")}
                    >
                        {linkify(fields.inspirationUrls)}
                    </SummaryCardItem>
                )}

                <SummaryCardItem title={__('Do you have a strict deadline for this project?')}>
                    {fields.timeline ? timeline(fields.timeline) : __('No')}
                </SummaryCardItem>
            </SummaryCard>

            <SummaryCard
                heading={__('About you')}
                onEditClick={() => handleEditClick('AboutYou')}
                url={routes.brief(brief, 2)}
            >
                {fields.organizationSize && (
                    <SummaryCardItem title={__('What is the size of your organization?')}>
                        {organizationSize(fields.organizationSize)}
                    </SummaryCardItem>
                )}
                {fields.industry && (
                    <SummaryCardItem title={__('What is your industry?')}>
                        {getIndustry(fields.industry)}
                    </SummaryCardItem>
                )}
                {fields.organizationRole && (
                    <SummaryCardItem title={__('What is your role?')}>
                        {fields.organizationRole}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        </ContentSetting>
    );
};

export default SummaryPage;
